import React from 'react';
import { format, parseISO } from 'date-fns';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Eye, MousePointerClick, MapPin, Clock, Globe } from 'lucide-react';

/**
 * Displays email tracking badges (Opened/Not Opened/Clicked) with
 * a hover popover showing open event details (date, time, location).
 */
export default function EmailTrackingBadges({ comm, compact = false }) {
  if (comm.type !== 'email') return null;
  const isOutbound = comm.direction !== 'inbound';
  const isSent = comm.email_sent === true;
  const hasTracking = !!comm.email_track_id;

  if (!isOutbound || !isSent) return null;

  const openEvents = comm.email_open_events || [];

  return (
    <>
      {/* Opened badge with popover */}
      {comm.email_opened && (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="inline-flex">
              <Badge className="bg-emerald-100 text-emerald-700 text-xs flex items-center gap-1 cursor-pointer hover:bg-emerald-200 transition-colors">
                <Eye className="h-3 w-3" /> Opened{comm.email_open_count > 1 ? ` ${comm.email_open_count}x` : ''}
              </Badge>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3" side="bottom" align="start">
            <div className="space-y-2">
              <p className="font-medium text-sm text-slate-800 flex items-center gap-1.5">
                <Eye className="h-4 w-4 text-emerald-600" />
                Email Opens {comm.email_open_count > 0 && <span className="text-slate-400 font-normal">({comm.email_open_count})</span>}
              </p>
              {openEvents.length > 0 ? (
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {openEvents.map((evt, i) => (
                    <div key={i} className="text-xs border-l-2 border-emerald-300 pl-2 py-1 space-y-0.5">
                      <p className="flex items-center gap-1 text-slate-700">
                        <Clock className="h-3 w-3 text-slate-400" />
                        {evt.timestamp ? format(parseISO(evt.timestamp), 'MMM d, yyyy h:mm a') : 'Unknown time'}
                      </p>
                      {(evt.city || evt.region || evt.country) && (
                        <p className="flex items-center gap-1 text-slate-500">
                          <MapPin className="h-3 w-3 text-slate-400" />
                          {[evt.city, evt.region, evt.country].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">
                  {comm.email_opened_at
                    ? `First opened ${format(parseISO(comm.email_opened_at), 'MMM d, yyyy h:mm a')}`
                    : 'No detailed open data available'}
                </p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Not opened badge */}
      {!comm.email_opened && hasTracking && (
        <Badge className="bg-slate-100 text-slate-500 text-xs">Not opened</Badge>
      )}

      {/* Clicked badge */}
      {comm.email_clicked && (
        <Badge className="bg-violet-100 text-violet-700 text-xs flex items-center gap-1">
          <MousePointerClick className="h-3 w-3" /> Clicked{comm.email_click_count > 1 ? ` ${comm.email_click_count}x` : ''}
        </Badge>
      )}
    </>
  );
}
